import { Box, Stack, Typography } from "@mui/material";
import React, { FC, useMemo, useRef, useState } from "react";
import { TPagination } from "@/types/api";
import {
  TCompanyFilterFormExtended,
  TCompanyDisplayColumnSetting,
} from "@/features/company/components/company-list/CompanyFilterDisplaySettingsModal";
import { CompanyFilterConditionPanel } from "@/features/company/components/company-list/CompanyFilterConditionPanel";
import { FilterConditionToolbar, TSavedCondition } from "@irohana-dev/components";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";


import {
  defaultCompanyFilterValues,
  defaultCompanyDisplayColumns,
  normalizeCompanyDateFields,
  normalizeCompanyMultiSelectFields,
} from "@/features/company/components/company-list/company-filter-shared";
import { applyColumnSettings } from "@/features/company/components/company-list/company-filter-condition-adapter";
import { COMPANY_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/company/constants/common";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { hasDisplayColumnsChanged } from "@/components/commons/filter-condition/filter-condition-adapter-kit";

type Props = {
  pagination?: TPagination;
  onApplyAdvancedFilters?: (filters: TCompanyFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TCompanyDisplayColumnSetting[]) => void;
  appliedConditionName?: string;
  appliedConditionId?: number | null;
  onAppliedConditionNameChange?: (name: string) => void;
  onAppliedConditionIdChange?: (id: number | null) => void;
  currentDisplayColumns?: TCompanyDisplayColumnSetting[] | null;
  currentAdvancedFilters?: TCompanyFilterFormExtended | null;
};

/** The 「デ」 badge uses the project primary for both scopes, on its lightest tint. */

export const CompanyListTop: FC<Props> = ({
  pagination,
  onApplyAdvancedFilters,
  onApplyDisplaySettings,
  appliedConditionName,
  appliedConditionId,
  onAppliedConditionNameChange,
  onAppliedConditionIdChange,
  currentDisplayColumns,
  currentAdvancedFilters,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const justSavedConditionRef = useRef(false);

  const handleFilterPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  const handleOpenCreateFilter = () => {
    setFilterMode("create");
    setFilterAnchorEl(document.body);
  };

  const handleOpenEditFilter = () => {
    setFilterMode("edit");
    setFilterAnchorEl(document.body);
  };

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const { registrationDateFrom, registrationDateTo, isShowCompanyArchive, ...rest } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v !== null && v !== undefined;
    }).length;
    if (registrationDateFrom || registrationDateTo) count += 1;
    if (isShowCompanyArchive) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteSavedCondition = useDeleteSavedCondition();
  const { data: savedConditionsData } = useSavedConditionList("company");

  const savedConditions = useMemo<TSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        // `id` is an int8 column, which the pg driver serializes as a string; the picker
        // matches its selection with ===, so it has to be a number on both sides.
        id: Number(item.id),
        name: item.title,
        // Conditions saved before the sharing columns existed carry no scope; the picker
        // groups strictly by it, so they would render in neither group.
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  // A condition that stored 項目設定 owns the columns too, so picking it restores them.
  // One that did not keeps whatever the user currently has.
  const applySavedColumnSettings = (picked: { includeColumnSettings?: boolean; columnSettings?: unknown }) => {
    if (!picked.includeColumnSettings || !picked.columnSettings) return;
    const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
    const base = currentDisplayColumns ?? [...defaultCompanyDisplayColumns];
    const next = applyColumnSettings(base, settings.order ?? [], settings.hidden ?? []);
    onApplyDisplaySettings?.(next);
    localStorage.setItem(COMPANY_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(next));
  };

  const handleSelectCondition = (id: string | number) => {
    const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
    if (!picked) return;
    // Saved conditions predate the multi-select fields, so their scalars are normalized
    // before they reach the query — the same treatment the panel gives them.
    onApplyAdvancedFilters?.({
      ...defaultCompanyFilterValues,
      ...normalizeCompanyMultiSelectFields(normalizeCompanyDateFields(picked.filters ?? {})),
    } as TCompanyFilterFormExtended);
    applySavedColumnSettings(picked);
    onAppliedConditionNameChange?.(picked.title);
    onAppliedConditionIdChange?.(Number(picked.id));
  };

  const handleDeleteAppliedCondition = async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      // The condition is gone, so the list must fall back to an unfiltered view.
      onApplyAdvancedFilters?.({ ...defaultCompanyFilterValues });
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  // Applying from create mode produces an unsaved ad-hoc filter, so the previously
  // applied condition's name/id must not stay attached to it.
  const handleApplyFiltersFromPanel = (filters: TCompanyFilterFormExtended) => {
    onApplyAdvancedFilters?.(filters);
    // A save applies its own condition right after this, so the create-mode reset would
    // wipe the id/name that were just set.
    if (justSavedConditionRef.current) {
      justSavedConditionRef.current = false;
      return;
    }
    if (filterMode === "create") {
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    }
  };

  // The saved condition becomes the applied one, so the toolbar shows its name instead
  // of the placeholder. Create then behaves as edit, so re-saving updates it.
  const handleConditionSaved = (id: number, title: string) => {
    justSavedConditionRef.current = true;
    onAppliedConditionNameChange?.(title);
    onAppliedConditionIdChange?.(id);
    setFilterMode("edit");
  };

  // Editing shows the condition's own sharing settings; creating starts from the defaults.
  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.(defaultCompanyFilterValues);
    onAppliedConditionNameChange?.("");
    onAppliedConditionIdChange?.(null);
    onApplyDisplaySettings?.([...defaultCompanyDisplayColumns]);
    localStorage.setItem(COMPANY_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(defaultCompanyDisplayColumns));
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body14Semi">
            全 {formatTotal(pagination?.total) ?? 0} 件中{" "}
            {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)} 件を表示
          </Typography>

          {/* Toolbar per the design: filter chip, saved-condition picker, then edit/delete. */}
          <FilterConditionToolbar
            onOpenFilter={handleOpenCreateFilter}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(currentDisplayColumns, defaultCompanyDisplayColumns))
            }
            conditions={savedConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectCondition}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={handleOpenEditFilter}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />
        </Stack>

      </Box>

      <CompanyFilterConditionPanel
        mode={filterMode}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        isOpen={Boolean(filterAnchorEl)}
        onClose={handleFilterPopoverClose}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={onApplyDisplaySettings}
        initialDisplayColumns={currentDisplayColumns || undefined}
        initialFilters={filterMode === "edit" ? currentAdvancedFilters || undefined : defaultCompanyFilterValues}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
